import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from '@/routing/navigation'
import { mutate } from 'swr'
import { ArchiveRestore, AlertCircle, ArrowRight, Loader2 } from '@digital-twins/design-system/icons'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { LockedFreeGate } from '@/components/billing/LockedFreeGate'
import { TrialAudienceUpgradeModal } from '@/components/billing/TrialAudienceUpgradeModal'
import { Skeleton } from '@/components/ui/skeleton'
import { audienceImportSessionsApi, populationsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { defaultAudienceTierForType } from '@/lib/utils/audience-tier'
import { useProfile } from '@/lib/hooks'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import type { AudienceCreationFlowSnapshot } from '@/types/components/audiences/import-session'
import { ImportWizardShell } from '@/layouts/AudienceImportSessionLayout/components/ImportWizardShell'
import { ImportActionBar } from '@/layouts/AudienceImportSessionLayout/components/ImportActionBar'
import type { ImportStepDefinition } from '@/types/components/audiences/import-shell'
import { AudienceDetailsCard } from './components/AudienceDetailsCard'
import { DatasetUploadCard } from './components/DatasetUploadCard'
import { WelcomeScreen } from './components/WelcomeScreen'
import useSWR from 'swr'
import { useLatestSession } from '@/layouts/AudienceImportSessionLayout/hooks/useLatestSession'
import { useAudienceImportSessionMutations } from '@/layouts/AudienceImportSessionLayout/hooks/useAudienceImportSession'
import { readImportSessionRestoreBeacon } from '@/layouts/AudienceImportSessionLayout/hooks/useImportSessionRestoreBeacon'
import { getAudienceImportWorkbookProjectionKey } from '@/layouts/AudienceImportSessionLayout/lib/cache-keys'
import {
  onImportSessionUpdated,
  subscribeToImportSession,
  unsubscribeFromImportSession,
} from '@/lib/services/websocket'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import type { AudienceImportWorkbookProjection } from '@/types/components/audiences/import-session'
import { buildNewAudienceEntrySteps } from '@/layouts/AudienceImportSessionLayout/lib/build-import-steps'
import { getAudienceImportFlowSnapshotKey } from '@/layouts/AudienceImportSessionLayout/lib/cache-keys'
import {
  navigateToImportRoute,
  resolveImportResumeTargetRoute,
} from '@/layouts/AudienceImportSessionLayout/lib/import-session-route-handoff'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import { usePreflightProgress } from './hooks/usePreflightProgress'
import i18n, { defaultContentLanguageFromUi } from '@/i18n'
import { useTranslation } from 'react-i18next'

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024
const ALLOWED_EXTENSIONS = new Set(['csv', 'sav', 'xlsx', 'xlsm', 'xls', 'dta', 'parquet'])
const EXCEL_EXTENSIONS = new Set(['xlsx', 'xlsm', 'xls'])
const CLIENT_SHEET_PARSE_MAX_BYTES = 10 * 1024 * 1024

async function readExcelSheetNames(file: File): Promise<Array<string>> {
  if (file.size > CLIENT_SHEET_PARSE_MAX_BYTES) {
    return []
  }
  const { read } = await import('xlsx')
  const buffer = await file.arrayBuffer()
  const wb = read(buffer, { bookSheets: true })
  return wb.SheetNames
}

function getFileExtension(file: File) {
  return file.name.split('.').pop()?.toLowerCase() ?? ''
}

function validateSelectedFile(file: File | null) {
  if (!file) {
    return i18n.t('audienceCreate.chooseFile', { ns: 'app' })
  }
  if (!ALLOWED_EXTENSIONS.has(getFileExtension(file))) {
    return i18n.t('audienceCreate.badType', { ns: 'app' })
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return i18n.t('audienceCreate.tooLarge', { ns: 'app' })
  }
  return null
}

export default function NewAudiencePage() {
  const { t, i18n } = useTranslation('app')
  const router = useRouter()
  const searchParams = useSearchParams()
  const bootstrapRequestIdRef = useRef(crypto.randomUUID())
  const createInFlightRef = useRef(false)
  const submitGenerationRef = useRef(0)
  const { data: profile, isLoading: profileLoading } = useProfile()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isResuming, setIsResuming] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [limitReached, setLimitReached] = useState(false)
  const [limitMessage, setLimitMessage] = useState<string | null>(null)
  const [restoreSessionId, setRestoreSessionId] = useState<string | null>(null)
  const [resumeDismissed, setResumeDismissed] = useState(false)
  const [startedSessionId, setStartedSessionId] = useState<string | null>(null)
  const [welcomePhase, setWelcomePhase] = useState<'visible' | 'exiting' | 'done'>('visible')

  // Audience-creation gate: anyone whose plan does NOT permit audience
  // creation (regular trial, locked_free) sees the upgrade modal instead
  // of the wizard — which would otherwise greet them with the destructive
  // `limitReached` alert because their tier has `maxAudiences: 0`.
  // Tiers with `featureFlags.createAudience: true` (pro, enterprise,
  // partner_trial) pass through into the normal wizard.
  // `?preview=trial-upgrade` force-opens the modal for design / PM
  // review — safe in all envs because the modal has no side effects
  // until a CTA fires.
  const cannotCreateAudiences = profile?.featureFlags?.createAudience === false
  const previewUpgrade = searchParams.get('preview') === 'trial-upgrade'
  const [upgradeOpen, setUpgradeOpen] = useState(previewUpgrade)

  const handlePreflightComplete = useCallback(
    (snapshot: AudienceCreationFlowSnapshot) => {
      createInFlightRef.current = false
      void mutate(
        getAudienceImportFlowSnapshotKey(snapshot.sessionId),
        snapshot,
        { revalidate: false },
      )
      navigateToImportRoute({
        sessionId: snapshot.sessionId,
        sourceRoute: '/audiences/new',
        targetRoute: snapshot.route,
        router,
        trigger: 'bootstrap_create',
      })
    },
    [router],
  )

  const handlePreflightError = useCallback((message: string) => {
    setError(message)
    createInFlightRef.current = false
  }, [])

  const { progress, startPhase1, enterPhase2, reset: resetProgress } =
    usePreflightProgress({
      onComplete: handlePreflightComplete,
      onError: handlePreflightError,
    })

  const isSubmitting = progress !== null

  function abandonInFlightSubmit() {
    submitGenerationRef.current += 1
    createInFlightRef.current = false
    resetProgress()
    setStartedSessionId(null)
  }

  function mintBootstrapRequestId() {
    bootstrapRequestIdRef.current = crypto.randomUUID()
  }

  const {
    latestSession,
    mutate: mutateLatestSession,
  } = useLatestSession({
    revalidateOnFocus: true,
  })
  const [formData, setFormData] = useState({
    name: '',
    audienceType: '' as '' | 'B2B' | 'B2C',
    sharingMode: 'organization' as 'organization' | 'private',
    tier: 'tier2' as 'tier1' | 'tier2' | 'tier3',
    language: defaultContentLanguageFromUi(),
  })
  const audienceLanguageTouchedRef = useRef(false)

  useEffect(() => {
    if (audienceLanguageTouchedRef.current) {
      return
    }
    const next = defaultContentLanguageFromUi(i18n.language)
    setFormData((previous) =>
      previous.language === next ? previous : { ...previous, language: next },
    )
  }, [i18n.language])

  /** Sheet names parsed client-side from the selected Excel file (before upload). */
  const [localSheetNames, setLocalSheetNames] = useState<Array<string>>([])
  const [isParsingSheets, setIsParsingSheets] = useState(false)

  /** When set, we stay on step 1 and show inline sheet selection instead of navigating. */
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null)
  const [pendingMachineVersion, setPendingMachineVersion] = useState<number | undefined>(undefined)
  const [audienceSheet, setAudienceSheet] = useState<string>('')
  const [dictionarySheet, setDictionarySheet] = useState<string | null>(null)

  const workbookKey = pendingSessionId
    ? getAudienceImportWorkbookProjectionKey(pendingSessionId)
    : null
  const { data: workbookProjection, mutate: mutateWorkbookProjection } = useSWR<AudienceImportWorkbookProjection | null>(
    workbookKey,
    workbookKey && pendingSessionId
      ? () => audienceImportSessionsApi.getWorkbookProjection(pendingSessionId)
      : null,
    { refreshInterval: legacyRefreshInterval('IMPORT', 1500), revalidateOnFocus: false, dedupingInterval: 1000 },
  )
  useEffect(() => {
    if (!pendingSessionId) {
      return
    }
    subscribeToImportSession(pendingSessionId)
    const offUpdated = onImportSessionUpdated(pendingSessionId, () => {
      void mutateWorkbookProjection()
    })
    return () => {
      offUpdated?.()
      unsubscribeFromImportSession(pendingSessionId)
    }
  }, [mutateWorkbookProjection, pendingSessionId])
  const sheetMachineVersion = workbookProjection?.machineVersion ?? pendingMachineVersion
  const { assignSheets, isAssigningSheets } = useAudienceImportSessionMutations(pendingSessionId ?? '', {
    fallbackMachineVersion: sheetMachineVersion,
  })

  const sheetAssignment = workbookProjection?.sheetAssignment
  const initialSheetsSetRef = useRef(false)
  useEffect(() => {
    if (!sheetAssignment) {
      initialSheetsSetRef.current = false
      return
    }
    if (!initialSheetsSetRef.current) {
      const opts = sheetAssignment.options
      setAudienceSheet(sheetAssignment.audienceSheet || opts[0]?.sheetName || '')
      setDictionarySheet(sheetAssignment.dictionarySheet ?? (opts.length > 1 ? opts[1].sheetName : null))
      initialSheetsSetRef.current = true
    }
  }, [sheetAssignment])
  useEffect(() => {
    if (!pendingSessionId) {
      initialSheetsSetRef.current = false
      setPendingMachineVersion(undefined)
    }
  }, [pendingSessionId])

  useEffect(() => {
    const prefillName = searchParams.get('name')
    if (prefillName) {
      setFormData((previous) => ({ ...previous, name: prefillName }))
    }
  }, [searchParams])

  useEffect(() => {
    populationsApi
      .canCreate()
      .then((response) => {
        if (!response.allowed) {
          setLimitReached(true)
          setLimitMessage(
            `You have reached your audience limit (${response.current}/${response.limit}). Upgrade or delete an existing audience before creating another one.`,
          )
        } else {
          setLimitReached(false)
          setLimitMessage(null)
        }
      })
      .catch(() => {
        // Backend create still enforces limits. This preflight is best-effort only.
      })
  }, [])

  useEffect(() => {
    const beacon = readImportSessionRestoreBeacon()
    setRestoreSessionId(beacon?.sessionId ?? null)
  }, [])

  const uploadSteps = useMemo<ImportStepDefinition[]>(() => {
    return buildNewAudienceEntrySteps()
  }, [])

  const isRestoreTarget =
    latestSession?.sessionId != null && latestSession.sessionId === restoreSessionId
  const showRestoreBanner =
    latestSession != null &&
    !resumeDismissed &&
    progress === null &&
    latestSession.sessionId !== startedSessionId
  const canStartImport =
    Boolean(selectedFile) &&
    !fileError &&
    formData.name.trim().length > 0 &&
    (formData.audienceType === 'B2B' || formData.audienceType === 'B2C') &&
    !limitReached &&
    !isSubmitting

  const showLocalSheetPicker = localSheetNames.length > 1 && !pendingSessionId
  const showSheetSelection = showLocalSheetPicker || Boolean(pendingSessionId && sheetAssignment)
  const canContinueSheets = audienceSheet.trim().length > 0 && !isAssigningSheets && !isSubmitting

  const hasPrefill = searchParams.get('name') != null
  const shouldShowWelcome = !hasPrefill
  const enteredFromWelcome = welcomePhase === 'done'

  function handleWelcomeDismiss() {
    // Race guard: if the profile hasn't resolved yet, treating the user as
    // permitted would drop them into the wizard where they'd hit the red
    // `limitReached` alert. Block the dismiss until we know feature flags.
    if (profileLoading) return
    if (cannotCreateAudiences) {
      setUpgradeOpen(true)
      return
    }
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
    if (prefersReduced) {
      setWelcomePhase('done')
      return
    }
    setWelcomePhase('exiting')
  }

  function handleFilesSelected(files: FileList) {
    const file = files.item(0)
    abandonInFlightSubmit()
    mintBootstrapRequestId()
    setStartedSessionId(null)
    setError(null)
    setPendingSessionId(null)
    setLocalSheetNames([])
    setAudienceSheet('')
    setDictionarySheet(null)
    const nextError = validateSelectedFile(file)
    if (nextError) {
      setSelectedFile(null)
      setFileError(nextError)
      return
    }
    setSelectedFile(file)
    setFileError(null)

    if (file && EXCEL_EXTENSIONS.has(getFileExtension(file))) {
      setIsParsingSheets(true)
      void readExcelSheetNames(file)
        .then((names) => {
          if (names.length > 1) {
            setLocalSheetNames(names)
            setAudienceSheet(names[0])
            setDictionarySheet(names.length > 1 ? names[1] : null)
          }
        })
        .catch(() => {
          // Client-side parse failed; fall back to the server-driven flow.
        })
        .finally(() => {
          setIsParsingSheets(false)
        })
    }
  }

  function clearFile() {
    abandonInFlightSubmit()
    mintBootstrapRequestId()
    setStartedSessionId(null)
    setSelectedFile(null)
    setFileError(null)
    setError(null)
    setPendingSessionId(null)
    setLocalSheetNames([])
    setIsParsingSheets(false)
    setAudienceSheet('')
    setDictionarySheet(null)
  }

  async function handleResumeImport() {
    if (!latestSession?.sessionId) {
      return
    }
    setIsResuming(true)
    setError(null)
    try {
      const snapshot = await audienceImportSessionsApi.getFlowSnapshot(
        latestSession.sessionId,
      )
      const targetRoute = resolveImportResumeTargetRoute({
        sessionId: latestSession.sessionId,
        snapshot,
        latestSessionRoute: latestSession.route,
      })
      if (!targetRoute?.trim()) {
        setError(
          t('audienceCreate.sessionNoRoute'),
        )
        void mutateLatestSession(undefined, { revalidate: false })
        return
      }
      await mutate(
        getAudienceImportFlowSnapshotKey(latestSession.sessionId),
        snapshot,
        { revalidate: false },
      )
      const navigated = navigateToImportRoute({
        sessionId: latestSession.sessionId,
        sourceRoute: '/audiences/new',
        targetRoute,
        router,
        trigger: 'resume_latest_session',
      })
      if (!navigated) {
        setError(
          t('audienceCreate.sessionNoRoute'),
        )
        void mutateLatestSession(undefined, { revalidate: false })
      }
    } catch (resumeError) {
      void mutateLatestSession(undefined, { revalidate: false })
      setError(
        getApiErrorMessage(
          resumeError,
          t('audienceCreate.sessionGone'),
        ),
      )
    } finally {
      setIsResuming(false)
    }
  }

  async function handleSheetContinue() {
    if (!audienceSheet.trim()) return

    // Server-driven path: session already exists from a prior upload.
    if (pendingSessionId) {
      setError(null)
      startPhase1()
      try {
        const ack = await assignSheets({
          audienceSheet: audienceSheet.trim(),
          dictionarySheet: dictionarySheet ?? undefined,
        })
        const flow = ack?.flowSnapshot
        setPendingSessionId(null)
        if (!flow?.route?.trim()) {
          resetProgress()
          return
        }
        enterPhase2(pendingSessionId, flow as AudienceCreationFlowSnapshot)
      } catch (err) {
        resetProgress()
        setError(getApiErrorMessage(err, t('audienceCreate.saveSheetFailed')))
      }
      return
    }

    // Client-side sheet picker path: upload the file, then assign sheets.
    await handleStartImport()
  }

  async function handleStartImport() {
    if (createInFlightRef.current) return
    createInFlightRef.current = true
    const generation = submitGenerationRef.current

    const normalizedAudienceType =
      formData.audienceType === 'B2B' || formData.audienceType === 'B2C'
        ? formData.audienceType
        : null

    const nextFileError = validateSelectedFile(selectedFile)
    if (nextFileError) {
      createInFlightRef.current = false
      setFileError(nextFileError)
      return
    }
    if (!formData.name.trim()) {
      createInFlightRef.current = false
      setError(t('audienceCreate.nameRequired'))
      return
    }
    if (!normalizedAudienceType) {
      createInFlightRef.current = false
      setError(t('audienceCreate.typeRequired'))
      return
    }
    if (limitReached) {
      createInFlightRef.current = false
      setError(limitMessage ?? t('audienceCreate.unavailable'))
      return
    }

    const hasPreSelectedSheets = localSheetNames.length > 1 && audienceSheet.trim().length > 0

    startPhase1()
    setError(null)
    try {
      const payload = new FormData()
      payload.append('file', selectedFile!)
      payload.append('name', formData.name.trim())
      payload.append('audienceType', normalizedAudienceType)
      payload.append('tier', formData.tier)
      payload.append('sharingMode', formData.sharingMode)
      payload.append('language', formData.language || 'en')

      const snapshot = await audienceImportSessionsApi.create(
        payload,
        bootstrapRequestIdRef.current,
      )
      if (generation !== submitGenerationRef.current) return
      setStartedSessionId(snapshot.sessionId)
      await mutate(getAudienceImportFlowSnapshotKey(snapshot.sessionId), snapshot, {
        revalidate: false,
      })
      track(AnalyticsEvents.AUDIENCE_IMPORT_SESSION_STARTED, {
        session_id: snapshot.sessionId,
        audience_type: normalizedAudienceType,
        tier: formData.tier,
        sharing_mode: formData.sharingMode,
        language: formData.language,
      })

      const needsSheetSelection = snapshot.route?.endsWith('/workbook-assignment') ?? false

      // Sheets were pre-selected client-side — assign them immediately after upload.
      if (needsSheetSelection && hasPreSelectedSheets) {
        setPendingSessionId(snapshot.sessionId)
        setPendingMachineVersion(snapshot.machineVersion)

        try {
          const ack = await audienceImportSessionsApi.assignSheets(
            snapshot.sessionId,
            {
              audienceSheet: audienceSheet.trim(),
              dictionarySheet: dictionarySheet ?? undefined,
            },
            snapshot.machineVersion,
          )
          if (generation !== submitGenerationRef.current) return
          const flow = ack?.flowSnapshot
          setPendingSessionId(null)
          setLocalSheetNames([])
          if (!flow?.route?.trim()) {
            resetProgress()
            createInFlightRef.current = false
            return
          }
          enterPhase2(snapshot.sessionId, flow as AudienceCreationFlowSnapshot)
        } catch (assignError) {
          if (generation !== submitGenerationRef.current) return
          resetProgress()
          createInFlightRef.current = false
          return
        }
        return
      }

      // Excel files that need sheet assignment but no local sheets — show server-driven picker.
      if (needsSheetSelection) {
        setPendingSessionId(snapshot.sessionId)
        setPendingMachineVersion(snapshot.machineVersion)
        resetProgress()
        createInFlightRef.current = false
        return
      }

      enterPhase2(snapshot.sessionId, snapshot)
    } catch (submitError) {
      if (generation !== submitGenerationRef.current) return
      setError(getApiErrorMessage(submitError, t('audienceCreate.startImportFailed')))
      resetProgress()
      createInFlightRef.current = false
    }
  }

  if (shouldShowWelcome && welcomePhase !== 'done') {
    return (
      <ImportWizardShell>
        <div
          className={cn(
            'flex flex-1 flex-col',
            welcomePhase === 'exiting'
              ? 'motion-safe:animate-wizard-exit'
              : 'motion-safe:animate-wizard-enter',
          )}
          onAnimationEnd={() => {
            if (welcomePhase === 'exiting') setWelcomePhase('done')
          }}
        >
          <WelcomeScreen
            onGetStarted={handleWelcomeDismiss}
            showResumeBanner={showRestoreBanner}
            latestSessionName={latestSession?.audienceDraft?.name ?? null}
            isResuming={isResuming}
            onResumeImport={() => void handleResumeImport()}
            onDismissResume={() => setResumeDismissed(true)}
          />
        </div>
        <TrialAudienceUpgradeModal
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          hasActiveSubscription={profile?.hasActiveSubscription ?? false}
        />
      </ImportWizardShell>
    )
  }

  if (profileLoading) {
    return (
      <ImportWizardShell>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-[300px] rounded-2xl" />
      </ImportWizardShell>
    )
  }

  if (profile?.subscriptionTier === 'locked_free') {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[60vh]">
        <LockedFreeGate action={t('audienceCreate.lockedAction')} ctaLabel={t('audienceCreate.lockedCta')} />
      </div>
    )
  }

  const creditBalance = profile?.creditInfo?.balance ?? 0
  const allowOverage = profile?.creditInfo?.allowOverage ?? false
  if (creditBalance <= 0 && !allowOverage && profile) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[60vh]">
        <LockedFreeGate
          action={t('audienceCreate.outOfCreditsAction')}
          title={t('creation.outOfCreditsTitle')}
          description={t('creation.outOfCreditsBody')}
          ctaLabel={t('creation.goToBilling')}
          href="/settings?tab=billing"
        />
      </div>
    )
  }

  return (
    <ImportWizardShell
      steps={uploadSteps}
      className={enteredFromWelcome ? 'motion-safe:animate-wizard-enter' : undefined}
    >
      {limitReached && limitMessage ? (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{limitMessage}</AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {showRestoreBanner ? (
        <div
          data-testid="import-session-resume-banner"
          className="flex w-full items-center gap-2 rounded-2xl bg-card px-6 py-4"
        >
          <ArchiveRestore className="h-4 w-4 shrink-0 text-cta" />
          <p className="flex-1 min-w-0 truncate text-sm leading-[1.5] text-foreground dark:text-white">
            {t('audienceCreate.restorePrompt')}{' '}
            <span className="text-muted-foreground dark:text-muted-foreground">
              {latestSession.audienceDraft?.name ?? t('audienceCreate.importSession')}
            </span>
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
              size="sm"
              data-testid="import-session-resume-button"
              onClick={() => void handleResumeImport()}
              disabled={isResuming}
              className="rounded-md"
            >
              {isResuming ? t('audienceCreate.resuming') : t('audienceCreate.resume')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="import-session-resume-dismiss"
              onClick={() => setResumeDismissed(true)}
              className="text-sm font-medium text-cta hover:bg-transparent hover:text-cta/80"
            >
              {t('audienceCreate.dismiss')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex w-full flex-col gap-6">
        <AudienceDetailsCard
          name={formData.name}
          onNameChange={(name) =>
            setFormData((previous) => ({ ...previous, name }))
          }
          audienceType={formData.audienceType}
          sharingMode={formData.sharingMode}
          onAudienceTypeChange={(audienceType) =>
            setFormData((previous) => ({
              ...previous,
              audienceType,
              tier: defaultAudienceTierForType(audienceType || null),
            }))
          }
          onSharingModeChange={(sharingMode) =>
            setFormData((previous) => ({ ...previous, sharingMode }))
          }
          language={formData.language}
          onLanguageChange={(language) => {
            audienceLanguageTouchedRef.current = true
            setFormData((previous) => ({ ...previous, language }))
          }}
        />

        <DatasetUploadCard
          selectedFile={selectedFile}
          fileError={fileError}
          progress={!showSheetSelection ? progress : null}
          parsingSheets={isParsingSheets}
          onFilesSelected={handleFilesSelected}
          onFileClear={clearFile}
          sheetOptions={
            showLocalSheetPicker
              ? localSheetNames.map((name) => ({ sheetName: name }))
              : sheetAssignment?.options
          }
          audienceSheet={audienceSheet}
          dictionarySheet={dictionarySheet}
          onAudienceSheetChange={setAudienceSheet}
          onDictionarySheetChange={setDictionarySheet}
        />
      </div>

      {showSheetSelection ? (
        <ImportActionBar>
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="rounded-lg"
            onClick={() => { setPendingSessionId(null); clearFile(); setLocalSheetNames([]) }}
          >
            {t('audienceCreate.startOver')}
          </Button>
          <Button
            type="button"
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            size="lg"
            className={cn('rounded-lg', AUDIENCE_WIZARD_BUTTON_CLASS)}
            disabled={!canContinueSheets}
            onClick={() => void handleSheetContinue()}
          >
            {isSubmitting || isAssigningSheets ? (
              <>
                {isSubmitting ? t('audienceCreate.uploading') : t('audienceCreate.saving')}
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                {t('audienceCreate.continue')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </ImportActionBar>
      ) : (
        <ImportActionBar
          nextLabel={t('audienceCreate.addContext')}
          onNext={() => void handleStartImport()}
          nextDisabled={!canStartImport}
          nextLoading={isSubmitting}
          nextLoadingLabel={t('audienceCreate.uploading')}
        />
      )}
    </ImportWizardShell>
  )
}
